/**
 * Pure rules for the sidebar, the menu search and the header's page title.
 *
 * The role and capability filters (staffRoleModules, investorPortalRouteCapabilities) run FIRST and
 * are not touched here. Everything below works on whatever survived them, so a group with no visible
 * items never appears and nothing a user could not already open is ever offered.
 */

/** Path matches exactly or is a nested child of basePath. */
export function isPathMatch(pathname, basePath) {
  if (!pathname || !basePath) return false;
  return pathname === basePath || pathname.startsWith(`${basePath}/`);
}

const SOON_MARK = "/modules/in-development/";
export const isComingSoon = (item) => String(item?.path || "").includes(SOON_MARK);

// Order of the rules is the order of the groups. An empty label means "no header" (the top item).
const STAFF_GROUPS = [
  { label: "", prefixes: ["/super_admin/dashboard"] },
  { label: "People", prefixes: ["/super_admin/member_management", "/super_admin/staff_registration"] },
  { label: "Money", prefixes: ["/super_admin/contribution", "/super_admin/loan_management", "/super_admin/transaction_history"] },
  { label: "Engage", prefixes: ["/super_admin/communication", "/super_admin/event_calendar", "/super_admin/attendance", "/super_admin/automated_birthday", "/super_admin/eminutes"] },
  { label: "Growth", prefixes: ["/super_admin/investor_portal"] },
  { label: "Insights", prefixes: ["/super_admin/reports", "/super_admin/audit_log"] },
  { label: "Manage", prefixes: ["/super_admin/settings", "/super_admin/admin"] },
];

const MEMBER_GROUPS = [
  { label: "", prefixes: ["/member/dashboard"] },
  { label: "My money", prefixes: ["/member/loan_management", "/member/contribution", "/member/transaction_history"] },
  { label: "Community", prefixes: ["/member/communication", "/member/event", "/member/attendance", "/member/birthdays", "/member/eminutes"] },
  { label: "Invest", prefixes: ["/invest"] },
  { label: "Help", prefixes: ["/member/support"] },
];

const MORE = "More";

const isMemberPath = (path) => path.startsWith("/member") || path.startsWith("/invest");

/** Which group a path belongs to; anything unknown falls into "More" so it is never hidden. */
export function groupLabelFor(path) {
  const p = String(path || "");
  const rules = isMemberPath(p) ? MEMBER_GROUPS : STAFF_GROUPS;
  const rule = rules.find((r) => r.prefixes.some((prefix) => isPathMatch(p, prefix)));
  return rule ? rule.label : MORE;
}

function groupOrder(path) {
  const p = String(path || "");
  const rules = isMemberPath(p) ? MEMBER_GROUPS : STAFF_GROUPS;
  const i = rules.findIndex((r) => r.prefixes.some((prefix) => isPathMatch(p, prefix)));
  return i === -1 ? rules.length : i;
}

/**
 * Turn the already-filtered flat menu into sections.
 *  - old top-level `type: "section"` markers are dropped; sections are derived now
 *  - "coming soon" placeholders are pulled out into their own list
 *  - with fewer than two sections there is nothing to tell apart, so no headers (`showLabels`)
 */
export function buildMenu(items) {
  const list = Array.isArray(items) ? items.filter((item) => item && item.type !== "section") : [];
  const soon = list.filter(isComingSoon);
  const live = list.filter((item) => !isComingSoon(item));

  const byOrder = new Map();
  live.forEach((item) => {
    const order = groupOrder(item.path);
    if (!byOrder.has(order)) byOrder.set(order, { label: groupLabelFor(item.path), items: [] });
    byOrder.get(order).items.push(item);
  });

  const sections = [...byOrder.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, section]) => section);

  return { sections, soon, showLabels: sections.length >= 2 };
}

/** Every page a user can open from the menu, as flat entries for search and the page title. */
export function flattenMenu(items) {
  const entries = [];
  const seen = new Set();
  const push = (entry) => {
    const key = `${entry.path}|${entry.label}`;
    if (!entry.path || seen.has(key)) return;
    seen.add(key);
    entries.push(entry);
  };

  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!item || item.type === "section") return;
    const soon = isComingSoon(item);
    const group = soon ? "Coming soon" : groupLabelFor(item.path);
    const subs = (item.subMenu || []).filter((s) => s && s.type !== "section" && s.path && !s.disabled);
    if (subs.length) {
      subs.forEach((sub) => push({ label: sub.name, parent: item.name, path: sub.path, group, soon, aliases: sub.aliases || [] }));
    } else {
      push({ label: item.name, parent: null, path: item.path, group, soon, aliases: item.aliases || [] });
    }
  });
  return entries;
}

/** Small, forgiving search over the entries a user can see. Best matches first. */
export function searchMenu(entries, query, limit = 8) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return entries.slice(0, limit);
  const tokens = q.split(/\s+/);

  return entries
    .map((entry) => {
      const label = String(entry.label || "").toLowerCase();
      const aliases = (entry.aliases || []).join(" ").toLowerCase();
      const hay = `${label} ${String(entry.parent || "").toLowerCase()} ${String(entry.group || "").toLowerCase()} ${aliases}`;
      if (!tokens.every((t) => hay.includes(t))) return null;
      let score = 0;
      if (label === q) score += 100;
      if (label.startsWith(q)) score += 40;
      if (label.includes(q)) score += 20;
      if ((entry.aliases || []).some((a) => String(a).toLowerCase() === q)) score += 60;
      if (entry.soon) score -= 10;
      return { entry, score };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((r) => r.entry);
}

/** Where the current page sits in the menu: used for the header's small label and title. */
export function locatePage(items, pathname) {
  const entries = flattenMenu(items);
  const matches = entries.filter((e) => isPathMatch(pathname, e.path));
  if (!matches.length) return null;
  const best = matches.sort((a, b) => b.path.length - a.path.length)[0];
  return { title: best.label, crumb: best.parent || (best.group && best.group !== "More" ? best.group : "") };
}
